import { types, getParent, type IAnyModelType } from 'mobx-state-tree';
import ObjectType from './object_type';
import EventType from './event_type';
import Dependency, { type IDependency } from './dependency';
import { capitalize } from 'lodash';
import AttributeOwner from './attribute_owner';
import { modelReference } from '../refHelper';
import { flatten } from 'lodash';
import { TYPE_INFO as MPC_TYPE_INFO } from './mpc_config';
import Inheritance, { type IInheritance } from './inheritance';
import type { IMetaModel } from './index';
import type { IObjectType } from './object_type';
import type { IEventType } from './event_type';
import type { ITransition } from './transition';
import type { IAttribute } from './attribute';

// The propagation path is a chain of dependencies and inheritances back to the
// owned origin of the method.
type PathEdge = IDependency | IInheritance;

/**
 * The Method instance interface.
 *
 * HAND-DECLARED so the loose `modelReference` targets read as concrete instances,
 * so the self-referencing `viaMethod` views type, and so sibling views see each
 * other. Hand-declaring also stops Method from being pulled into the recursion
 * by the other models that hold `IMethod[]`.
 */
export interface IMethod {
  readonly id: string;
  readonly name: string;
  readonly ownerEventType: IEventType;
  readonly ownerObjectType: IObjectType;
  readonly provenance: 'OWNED' | 'ACQUIRED' | 'INHERITED' | 'SPECIALISED';
  readonly type: 'CREATE' | 'MODIFY' | 'END';
  readonly viaMethod: IMethod | null;
  readonly viaDependency: IDependency | null;
  readonly viaInheritance: IInheritance | null;
  readonly precondition: string;
  readonly postcondition: string;
  readonly implementation: string;
  readonly attributes: IAttribute[];
  readonly metamodel: IMetaModel;
  readonly displayType: string;
  readonly displayString: string;
  readonly metadata: string;
  readonly displayStringWithMetadata: string;
  asString(): string;
  propagatedViaDependency(dependency: IDependency, seen?: IMethod[]): boolean;
  propagatedViaInheritance(inheritance: IInheritance, seen?: IMethod[]): boolean;
  findAcquiredMethod(dependency: IDependency | null): IMethod | null;
  findAcquiredMethods(): IMethod[];
  findInheritedMethod(inheritance: IInheritance | null): IMethod | null;
  findInheritedMethods(): IMethod[];
  isLooping(): boolean;
  readonly transitions: ITransition[];
  readonly pathToViaMethod: PathEdge[] | undefined;
  readonly propagationPath: PathEdge[] | undefined;
  readonly propagationPathMethods: IMethod[] | undefined;
  readonly secondary: boolean;
  readonly provenancePriority: number;
  readonly specialisation: IMethod | undefined;
  readonly inherited: boolean;
  readonly specialised: boolean;
  readonly hidden: boolean;
  findSpecialisedMethods(): IMethod[];
  getAttributeById(id: string): IAttribute | undefined;
  setName(name: string): void;
  setType(type: 'CREATE' | 'MODIFY' | 'END'): void;
  setPrecondition(precondition: string): void;
  setImplementation(implementation: string): void;
  setPostcondition(postcondition: string): void;
  specialise(viaMethodId: string): void;
  makeOwned(): void;
  removeAttribute(id: string): void;
  addAttribute(
    id: string,
    name: string,
    type: string,
    description: string,
    implementation: string,
    validation: string
  ): IAttribute;
}

const ORDERED_PROVENANCES = ['SPECIALISED', 'INHERITED', 'OWNED', 'ACQUIRED'];

/**
 * A MERODE method.
 *
 * This is only the 'base' for the MST model. It gets composed with the
 * AttributeOwner partial type below.
 *
 * For the references to other MST nodes, we don't use types.reference directly,
 * but our custom wrapper. See '../refHelper.js' for the reason why.
 */
const MethodBase = types
  .model('MethodBase', {
    id: types.identifier,
    name: types.string,
    ownerEventType: modelReference(EventType),
    ownerObjectType: modelReference(types.late((): IAnyModelType => ObjectType)),
    provenance: types.enumeration(['OWNED', 'ACQUIRED', 'INHERITED', 'SPECIALISED']),
    type: types.enumeration(['CREATE', 'MODIFY', 'END']),
    viaMethod: types.maybeNull(
      modelReference(types.late((): IAnyModelType => Method))
    ),
    viaDependency: types.maybeNull(modelReference(Dependency)),
    viaInheritance: types.maybeNull(
      modelReference(types.late((): IAnyModelType => Inheritance))
    ),
    precondition: types.optional(types.string, ''),
    postcondition: types.optional(types.string, ''),
    implementation: types.optional(types.string, ''),
  })
  .views((self0) => {
    const self = self0 as unknown as IMethod;
    return {
    // convenience method to get the metamodel
    get metamodel(): IMetaModel {
      // up 2-levels because 1 level up is the array of event types.
      return getParent(self0, 2);
    },
    // type for showing in the UI: OwnedMethod or AcquiredMethod
    get displayType() {
      return `${capitalize(self.provenance.toLowerCase())}Method`;
    },
    // Used in the logger and the report view
    get displayString() {
      return `${self.displayType} ${self.name}`;
    },
    // metadata: O/C, O/M, O/E, A/C, A/M, A/E
    get metadata() {
      return `${self.provenance[0]}/${self.type[0]}`;
    },
    // Used in the model explorer to show both metadata and name
    get displayStringWithMetadata() {
      return `${self.metadata} ${self.name}`;
    },
    // Human-readable label, e.g. for `${method}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.displayStringWithMetadata;
    },
    // Is this method propagated (directly or indirectly) via the given
    // dependency?
    propagatedViaDependency(dependency: IDependency, seen: IMethod[] = []): boolean {
      if (seen.includes(self)) {
        // Break the loop. Can only happen while trying to create/check an invalid model. We need to
        // return true to make the method propagation code stop.
        return true;
      } else if (self.provenance === 'OWNED') {
        return false;
      } else if (self.provenance === 'ACQUIRED' && self.viaDependency === dependency) {
        return true;
      } else {
        // Check the via method
        seen.push(self);
        return self.viaMethod!.propagatedViaDependency(dependency, seen);
      }
    },
    // Is this method propagated (directly or indirectly) via the given
    // inheritance?
    propagatedViaInheritance(inheritance: IInheritance, seen: IMethod[] = []): boolean {
      if (seen.includes(self)) {
        // Break the loop. Can only happen while trying to create/check an invalid model. We need to
        // return true to make the method propagation code stop.
        return true;
      } else if (self.provenance === 'OWNED') {
        return false;
      } else if (self.inherited && self.viaInheritance === inheritance) {
        return true;
      } else {
        // Check the via method
        seen.push(self);
        return self.viaMethod!.propagatedViaInheritance(inheritance);
      }
    },
    // find the acquired method that has the given dependency as its
    // viaDependency and this method as its viaMethod.
    findAcquiredMethod(dependency: IDependency | null) {
      if (dependency === null) {
        return null;
      } else {
        return (
          dependency.master.acquiredMethods.find(
            (m) => m.viaDependency === dependency && m.viaMethod === self
          ) || null
        ); // find returns undefined when not found
      }
    },
    // find all acquired methods that have this method as their viaMethod
    findAcquiredMethods() {
      return self.metamodel.acquiredMethods.filter((m) => m.viaMethod === self);
    },
    // find the inherited method that has the given inheritance as its
    // viaInheritance and this method as its viaMethod.
    findInheritedMethod(inheritance: IInheritance | null) {
      if (inheritance === null) {
        return null;
      } else {
        return inheritance.subtype.inheritedMethods.find((m) => m.viaMethod === self) || null; // find returns undefined when not found
      }
    },
    // find all inherited methods that have this method as their viaMethod
    findInheritedMethods() {
      return self.metamodel.inheritedMethods.filter((m) => m.viaMethod === self);
    },
    // Check whether this method is looping -> See the discussion on p. 190 of the book
    isLooping() {
      let dependenciesToCheck: IDependency[] = [];
      if (!self.specialised) {
        dependenciesToCheck = dependenciesToCheck.concat(
          self.ownerObjectType.dependenciesAsDependent
        );
      }
      if (!self.inherited) {
        dependenciesToCheck = dependenciesToCheck.concat(
          self.ownerObjectType.indirectSupertypes.flatMap((s) => s.dependenciesAsDependent.slice())
        );
      }

      for (const dep of dependenciesToCheck) {
        if (self.findAcquiredMethod(dep) === null && self.propagatedViaDependency(dep)) {
          return true;
        }
      }
      if (!self.specialised) {
        for (const inh of self.ownerObjectType.inheritancesAsSupertype) {
          if (self.findInheritedMethod(inh) === null && self.propagatedViaInheritance(inh)) {
            return true;
          }
        }
      }
      return false;
    },
    get transitions() {
      return flatten(
        self.ownerObjectType.fsms.map((f) => f.transitions.filter((t) => t.hasMethod(self)).slice())
      );
    },
    get pathToViaMethod(): PathEdge[] | undefined {
      if (self.provenance === 'OWNED' || self.provenance === 'SPECIALISED') {
        return [];
      } else if (self.provenance === 'INHERITED') {
        return [self.viaInheritance!];
      } else if (self.provenance === 'ACQUIRED') {
        return ([self.viaDependency!] as PathEdge[]).concat(
          // If the ownerObjectType of the viaMethod is not the same as the dependent of the
          // viaDependency, that means we are dealing with an inherited dependency and we need to
          // append the (reverse) of the inheritance path from the viaMethod's ownerObjectType to
          // the viaDependency's dependent.
          self.viaMethod!.ownerObjectType.inheritancePathTo(self.viaDependency!.dependent).reverse()
        );
      }
    },
    /**
     * Get the propagation path for a method = the path to the OWNED method that is the origin of
     * the method:
     * - OWNED: an empty path
     * - SPECIALISED: an empty path
     * - ACQUIRED: the path to the via method + propagation path of the via method
     * - INHERITED: the viaInheritance (= the path to the via method) + the propagation path of the
     *   inherited method
     */
    get propagationPath(): PathEdge[] | undefined {
      if (self.provenance === 'OWNED' || self.provenance === 'SPECIALISED') {
        return [];
      } else if (self.provenance === 'ACQUIRED' || self.provenance === 'INHERITED') {
        return self.pathToViaMethod!.concat(self.viaMethod!.propagationPath!);
      }
    },
    get propagationPathMethods(): IMethod[] | undefined {
      if (self.provenance === 'OWNED' || self.provenance === 'SPECIALISED') {
        return [];
      } else if (self.provenance === 'ACQUIRED' || self.provenance === 'INHERITED') {
        return [self.viaMethod!].concat(self.viaMethod!.propagationPathMethods!);
      }
    },
    get secondary() {
      if (self.viaMethod == null) {
        return false;
      } else {
        if (self.viaMethod.secondary) {
          return true;
        } else {
          const path = self.propagationPath;
          for (const mpc of self.metamodel.multiplePropagationConstraints) {
            if (mpc.type === MPC_TYPE_INFO.EQUAL_TO.key && mpc.secondaryPath.isPartOf(path)) {
              return true;
            }
          }
          return false;
        }
      }
    },
    get provenancePriority() {
      return ORDERED_PROVENANCES.indexOf(self.provenance);
    },
    get specialisation() {
      if (self.inherited) {
        return self.ownerObjectType.specialisedMethods.find((m) => m.viaMethod === self);
      } else {
        return undefined;
      }
    },
    get inherited() {
      return self.provenance === 'INHERITED';
    },
    get specialised() {
      return self.inherited && self.specialisation != null;
    },
    get hidden() {
      return self.ownerObjectType.abstract || self.specialisation != null;
    },
    findSpecialisedMethods() {
      if (self.inherited) {
        return self.metamodel.specialisedMethods.filter((m) => m.viaMethod === self);
      } else {
        return [];
      }
    },
    };
  })
  .actions((self) => ({
    setName(name: string) {
      self.name = name;
    },
    setType(type: 'CREATE' | 'MODIFY' | 'END') {
      self.type = type;
    },
    setPrecondition(precondition: string) {
      self.precondition = precondition;
    },
    setImplementation(implementation: string) {
      self.implementation = implementation;
    },
    setPostcondition(postcondition: string) {
      self.postcondition = postcondition;
    },
    specialise(viaMethodId: string) {
      self.provenance = 'SPECIALISED';
      // MST accepts an identifier string when writing a reference, but the TS
      // write type is the resolved instance, so we cast at the assignment.
      self.viaMethod = viaMethodId as unknown as typeof self.viaMethod;
    },
    makeOwned() {
      self.provenance = 'OWNED';
      self.viaMethod = null;
    },
  }));

// compose the base with Attribute Owner
const Method = types.compose('Method', MethodBase, AttributeOwner);

export default Method;
